import { type ReactNode } from 'react';

import { XStack } from '@/components/ui/layout/layout';

import { PersonAvatar } from '../components/person-avatar';
import { useOpenPerson } from '../people-wiki.query';

import { CollapsingRow } from './collapsing-row';
import { isCollapsed, type LineStatus } from './use-request-lines';

import type { WikiPerson } from '../people-wiki.types';

/** One line of the requests card: the person, a muted `detail` beside the name, and its buttons (`children`) at the end.
 * A tight line cuts the detail short with an ellipsis and keeps it whole as a title. A click elsewhere on the line opens
 * the page of the person. The line collapses with its status, and `onClosed` is called when it has closed. */
export const RequestLine = ({
  person,
  status,
  onClosed,
  detail,
  children,
}: {
  person: Pick<WikiPerson, 'id' | 'name' | 'avatarUrl' | 'primaryEmail'>;
  status: LineStatus;
  onClosed: () => void;
  detail: string;
  children: ReactNode;
}) => {
  const openPerson = useOpenPerson();
  const open = () => void openPerson(person.id);

  return (
    <CollapsingRow
      collapsed={isCollapsed(status)}
      held={status === 'connected'}
      onCollapsed={onClosed}
    >
      <XStack
        role="link"
        tabIndex={0}
        aria-label={`Open ${person.name}`}
        data-request-line
        className="h-11 cursor-pointer items-center gap-3 rounded-lg px-2.5 hover:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        onClick={open}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
      >
        <PersonAvatar person={person} sizeClassName="size-7" />
        <XStack className="min-w-0 flex-1 items-baseline gap-2">
          <span className="max-w-[50%] shrink-0 truncate text-sm text-foreground">
            {person.name}
          </span>
          <span
            className="min-w-0 truncate text-xs text-foreground-subtle"
            title={detail}
          >
            {detail}
          </span>
        </XStack>
        {/* The buttons answer the request, and must not open the page */}
        <XStack
          className="shrink-0 items-center gap-1"
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </XStack>
      </XStack>
    </CollapsingRow>
  );
};
